/// <amd-module name="Artifacts/Root/components/BadgeModal" />
import { ComponentState } from "Artifacts/Root/Constants";
import { registerModal } from "Artifacts/Root/containers/ModalManager";
import { Badge } from "Artifacts/Root/helpers/BadgeHelper";
import { getFeedIdentifier, getViewFromNameOrId } from "Artifacts/Root/helpers/FeedHelpers";
import { encodeName } from "Artifacts/Root/helpers/PackageDecoderHelper";
import { getCurrentSelectedProjectName } from "Artifacts/Root/helpers/ProjectHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { CopyableTextField } from "Artifacts/Root/protocols/components/CopyableTextField";
import { connectDynamicModules } from "Artifacts/Root/redux";
import { IRootModuleState } from "Artifacts/Root/redux/Contracts";
import { RootModule } from "Artifacts/Root/redux/Module";
import * as Res from "Artifacts/Root/Resources";
import { packageDetails } from "Artifacts/Root/routing/Constants";
import { IFeed, IPackage, IPackageVersionIdentity } from "Artifacts/Root/types";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";
import { Dropdown } from "VSSUI/Dropdown";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";

export interface IBadgeModalProps {
  packages?: IPackageVersionIdentity[];
  onDismiss: () => void;
}

interface IMapStateToProps {
  componentState: ComponentState;
  pkg?: IPackage;
}

export const BadgeModalId = "BadgeModal";

const BadgeModal: React.FC<IBadgeModalProps & IMapStateToProps> = ({ packages, onDismiss }) => {
  const { feed } = useFeed();

  const feedViews: ViewIdentifier[] = feed?.views || [];

  const viewItems = feedViews.map((view) => {
    return {
      id: view.id,
      text: `@${view.name}`,
    };
  });
  viewItems.push({
    id: "NoView",
    text: Res.NoView,
  });

  const [selectedView, setSelectedView] = useState<string>(viewItems[0]?.id);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const history = useHistory();

  const { pageContext } = useComponentContext();

  return (
    <Panel
      className="badge-modal"
      titleProps={{ text: Res.CreateBadgePanel_Header }}
      onDismiss={onDismiss}
    >
      {
        <div>
          {viewItems.length < 1 ? (
            <span> {Res.CreateBadgePanel_NoView}</span>
          ) : (
            <div className="flex-column">
              {errorMessage && (
                <MessageBar
                  className="badge-modal-block image-load-error"
                  severity={MessageBarSeverity.Warning}
                >
                  {errorMessage}
                </MessageBar>
              )}

              <div className="fontSizeM badge-modal-block">{Res.CreateBadgePanel_Description}</div>

              <div className="badge-modal-block">
                {Res.CreateBadgePanel_ViewDropdown_Label}
                <Dropdown
                  items={viewItems}
                  onSelect={(_event, item) => {
                    setErrorMessage(undefined);
                    setSelectedView(item.id);
                  }}
                  placeholder={Res.ViewDropdownPlaceholder}
                />
              </div>

              {!errorMessage && (
                <div>
                  <div className={"badge-modal-block flex-column"}>
                    {Res.CreateBadgePanel_Preview_Label}
                    {packages && feed && (
                      <Link
                        onClick={() => {
                          history.push(getPackageDetailsRelativePath(feed, selectedView, packages));
                          window.location.reload();
                        }}
                      >
                        <img
                          className={"preview-image"}
                          alt={StringUtils.format(
                            Res.CreateBadgePanel_BadgeImage_AltText_Preview,
                            packages[0].protocol,
                            packages[0].version,
                          )}
                          src={
                            getBadgeUri(pageContext, feed, packages[0], selectedView).absoluteUri
                          }
                          onError={() => {
                            setErrorMessage(getNoBadgeText(feed, packages[0], selectedView));
                          }}
                        />
                      </Link>
                    )}
                  </div>

                  <div className={"badge-modal-block flex-column"}>
                    {Res.CreateBadgePanel_Markdown_Label}
                    {packages && feed && (
                      <CopyableTextField
                        text={StringUtils.format(
                          "[![{0}]({1})]({2})",
                          getMarkdownAltText(feed, selectedView, packages[0]),
                          getBadgeUri(pageContext, feed, packages[0], selectedView).absoluteUri,
                          getFullPackageDetailsUrl(
                            pageContext,
                            feed.accountMetadata.publicAccessMapping,
                            getCurrentSelectedProjectName(pageContext),
                            getPackageDetailsRelativePath(feed, selectedView, packages),
                          ),
                        )}
                      />
                    )}
                  </div>

                  <div className={"badge-modal-block flex-column"}>
                    {Res.CreateBadgePanel_ImageLink_Label}
                    {packages && feed && (
                      <CopyableTextField
                        text={getBadgeUri(pageContext, feed, packages[0], selectedView).absoluteUri}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
    </Panel>
  );
};

function getPackageDetailsRelativePath(
  feed: IFeed,
  selectedView: string,
  packages: IPackageVersionIdentity[],
): string {
  return (
    generatePath(packageDetails, {
      feedIdentifier: getFeedIdentifier(feed, getViewFromNameOrId(feed, selectedView)),
      packageName: encodeName(packages[0].name, packages[0].protocol),
      protocol: packages[0].protocol,
    }) + "?preferRelease=true"
  );
}

function getBadgeUri(
  pageContext: IVssPageContext,
  feed: IFeed,
  selectedPackage: IPackageVersionIdentity,
  selectedView: string,
): Uri {
  // Making call in component because this component is used in both feed & package controller views/stores
  // Move it out once we have a single store.
  const projectId = feed?.project ? feed.project.id : null;
  const packageId = selectedPackage.packageId;
  return Badge.getPackageBadgeRestUri(
    pageContext,
    getFeedIdentifier(feed, getViewFromNameOrId(feed, selectedView)),
    packageId,
    projectId,
    feed.accountMetadata.publicAccessMapping,
  );
}

function getMarkdownAltText(
  feed: IFeed,
  selectedView: string,
  selectedPackage: IPackageVersionIdentity,
): string {
  const altText = StringUtils.format(
    Res.CreateBadgePanel_BadgeImage_AltText,
    selectedPackage.name,
    getFeedIdentifier(feed, getViewFromNameOrId(feed, selectedView)),
    Res.AzureArtifacts,
  );

  return altText;
}

function getNoBadgeText(
  feed: IFeed,
  selectedPackage: IPackageVersionIdentity,
  selectedView: string,
): string {
  const view = getViewFromNameOrId(feed, selectedView);
  if (view) {
    return StringUtils.format(
      Res.CreateBadgePanel_BadgeLoadError_View,
      selectedPackage.name,
      view.name,
    );
  }
  return StringUtils.format(Res.CreateBadgePanel_BadgeLoadError, selectedPackage.name);
}

function getFullPackageDetailsUrl(
  pageContext: IVssPageContext,
  publicAccessMapping: string,
  projectId: string | null,
  wildcard?: string,
): string | null {
  if (projectId == null) {
    return null;
  }

  try {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");

    let url = locationService.routeAbsoluteUrl("ms.azure-artifacts.artifacts-route", {
      project: projectId,
    });

    if (wildcard) {
      url = combineUrlPaths(url, wildcard);
    }

    return url;
  } catch (e) {
    return null;
  }
}

const mapStateToProps = (state: IRootModuleState): IMapStateToProps => {
  const rootState = state.rootState;
  return {
    componentState: rootState.promoteCommandState,
  };
};

const ConnectedBadgeModal = connectDynamicModules(connect(mapStateToProps)(BadgeModal), [
  RootModule,
]);

registerModal(BadgeModalId, ConnectedBadgeModal);

export { BadgeModal };
